// Client for the Gmail integration API (/api/gmail/*). Unlike team.js/events.js
// there is deliberately NO local-storage fallback here — connecting a real
// inbox can't be simulated, so when the API isn't reachable the UI shows an
// honest "unavailable" state instead of fake data.
//
// The acting user is identified server-side from the Cognito ID token we send
// as a Bearer header — never a request field — so a caller can only ever touch
// their own mailbox. The legacy `user` argument is accepted for call-site
// compatibility but is not what the server trusts.
import { getIdToken } from './cognito.js'

async function authHeaders(extra = {}) {
  const token = await getIdToken()
  return {
    ...extra,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

async function apiJson(res) {
  const ct = res.headers.get('content-type') || ''
  if (!ct.includes('application/json')) throw new Error('Gmail API unavailable')
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || `Gmail API error (${res.status})`)
  return data
}

// { available, configured, connected, address, connected_at }
export async function gmailStatus() {
  try {
    const data = await apiJson(
      await fetch('/api/gmail/status', { headers: await authHeaders() }),
    )
    return { available: true, ...data }
  } catch {
    return { available: false, configured: false, connected: false }
  }
}

// One conversation with every message body (plaintext, server-extracted).
export async function gmailThread(id) {
  return apiJson(
    await fetch(`/api/gmail/thread?id=${encodeURIComponent(id)}`, { headers: await authHeaders() }),
  )
}

// Returns the Google consent URL to redirect the browser to.
export async function gmailAuthUrl() {
  const data = await apiJson(
    await fetch('/api/gmail/auth-url', { headers: await authHeaders() }),
  )
  return data.url
}

/**
 * WHO THIS MAILBOX HAS WRITTEN TO — all of it, not the newest handful.
 *
 * /gmail/messages fetches per-message metadata one request at a time and is
 * capped at 15, so every "have we contacted them?" answer built on it was
 * judging a 1,400-message mailbox by its newest 15. This route indexes the
 * whole sent folder (headers only, concurrently, cached server-side and
 * rebuilt on a schedule) and answers { address, recipients: {addr: {last, n}},
 * messagesRead, truncated }.
 *
 * Fails soft: an unreachable index reports `checked: false` with the reason, so
 * a caller can say the lane was unread instead of reporting nobody was emailed.
 */
export async function gmailSentIndex({ fresh = false } = {}) {
  try {
    const params = fresh ? '?fresh=1' : ''
    const data = await apiJson(
      await fetch(`/api/gmail/sent-index${params}`, { headers: await authHeaders() }),
    )
    return { checked: true, ...data }
  } catch (err) {
    return { checked: false, reason: err?.message || 'the sent index could not be read', recipients: {} }
  }
}

// TODAY'S MEETINGS: GONE. calendarToday() read the Google Calendar behind this
// same connection for the daily-tasks briefing's section 4. That section was
// removed on 2026-09-03 at the owner's request — no calendar integration at
// all — and this was its only caller, so the client went with it. The lambda's
// `calendar` action is untouched and still answers; nothing in this app asks.
// Everything else in this file is the INBOX, which stays: reading it is what
// freezes a lead who has already written back out of the cadence.

export async function gmailMessages({ q = '', max = 15 } = {}) {
  const params = new URLSearchParams({ max: String(max) })
  if (q) params.set('q', q)
  return apiJson(
    await fetch(`/api/gmail/messages?${params}`, { headers: await authHeaders() }),
  )
}

// NO SEND HERE (2026-10-20): the app sends through Apollo only
// (lib/apolloSend.js). The gmail lambda's /gmail/send route still exists
// server-side, but nothing in the app calls it any more.

// Draft an outreach email with AI from a short brief. Returns
// { configured, subject, body } — when AI drafting isn't set up server-side the
// call still resolves with { configured: false } so the caller can hide the
// assist rather than surface an error. `brief` fields (all optional): to,
// company, recipient_name, city, trade, sender_name, tone, purpose.
export async function gmailDraft(brief) {
  return apiJson(
    await fetch('/api/gmail/draft', {
      method: 'POST',
      headers: await authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(brief),
    }),
  )
}

// Conversational workspace assistant (OpenAI server-side). `messages` is the
// chat history [{role, text}], `context` a snapshot of live workspace facts the
// answer must be grounded in. Returns { configured, text, nav } — when the
// OpenAI key isn't set up server-side it resolves with { configured: false }
// so the caller can fall back to deterministic answers instead of erroring.
export async function gmailAssistant({ messages, context }) {
  return apiJson(
    await fetch('/api/gmail/assistant', {
      method: 'POST',
      headers: await authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ messages, context }),
    }),
  )
}

export async function gmailDisconnect() {
  return apiJson(
    await fetch('/api/gmail/disconnect', {
      method: 'POST',
      headers: await authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({}),
    }),
  )
}
